/**
 * NarratedChecklist.tsx — the ONE renderer for the journey's narrated waits:
 * "We’re checking your answers" (`BuildingInterstitial`) and, since
 * 2026-09-25, "Setting up your health check" (`CaptureLoader`, Step 0).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (CTO, 2026-09-24; the capture checklist 2026-09-25).
 *
 * WHY ONE COMPONENT. The capture loader was asked for "in the same visual
 * language" as the checking loader, and two copies of a checklist drift the
 * first time either is restyled — a tick drawn one way on one screen and
 * another way on the next reads as two products. So the look, the motion and
 * the accessibility rules below live here once; each caller hands over its
 * steps, its words and the view its pacing engine (`journeyLoaders.ts`) is on.
 * What a step SAYS, and when it may tick, is never decided here.
 *
 * THE LOOK (premium pass, 2026-09-24 — "B's logic in A's open layout"). No
 * boxed card: the list sits on the page ground with a soft bloom behind the
 * heading, and a white row GLIDES to the current step. DONE = a light disc
 * whose tick draws in, its second line turning past-tense ("Saved"); CURRENT =
 * a thin turning ring, a bold label and a now-line saying what is happening;
 * UPCOMING = a quiet empty ring. The connector between marks fills as each step
 * completes. Then an "All done" beat shows the finished list before the next
 * screen replaces it.
 *
 * MISSED (2026-09-25, the capture loader). A step whose work did NOT happen —
 * only the lead save can fail — finishes without a tick: a light disc with a
 * short level bar, and the step's `missed` line in the muted ink instead of its
 * past-tense word. The list still moves on (the patient is never blocked), but
 * nothing on it claims a save that did not land. The status region SAYS so
 * too (review, 2026-09-25 — WCAG 4.1.3): the next announcement leads with the
 * missed line ("Not saved this time — we’ll carry on without it. Calculating
 * your projection"), because a screen-reader user otherwise heard exactly the
 * sequence a successful save produces, with the outcome only in the list.
 *
 * ⚠️ WHAT MOVES, AND WHAT MAY NOT. The glide is `transform` on the white row
 * itself — an empty leaf — and the connector fill is a pseudo-element: NOTHING
 * that wraps content transforms, filters or sets `contain` (a transformed
 * ancestor re-anchors `position: fixed` content and non-portaled dialogs).
 *
 * ACCESSIBILITY. ONE polite status region, visually hidden, carries the CURRENT
 * step's label — then, only while the wait is late, the caller's `waitLine`,
 * and finally the all-done line — so a screen reader hears each step once as
 * it starts rather than a chatter of every tick. The region MOUNTS EMPTY and
 * gains its first words {@link STATUS_ARM_MS} later (review, 2026-09-25): a
 * status region that appears with its text already in it is not reliably
 * announced, one that gains its text is — `BrandedLoader`'s rule, and the
 * reason its journey look mounts its region empty too. Without it the first
 * step was probably never heard (the capture loader also moves focus to the
 * heading in that same instant). `aria-busy` sits on the
 * checklist (the part being rebuilt) until the closing beat, and deliberately
 * NOT on the status region or any ancestor of it: a live region under a busy
 * element may be held silent, and this one is never "un-busied" (it unmounts).
 * The current row carries `aria-current="step"`. `focusTitle` puts focus on the
 * heading once, on mount — for a loader that REPLACES the control the patient
 * just pressed (Step 0's submit), so the keyboard is not left on `<body>`.
 * REDUCED MOTION: the same states on the same clock — the ring stands still,
 * the tick is simply there, the row jumps rather than glides, the connector is
 * simply filled.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { LoaderRing } from '../ui/LoaderRing';
import { usePrefersReducedMotion } from '../ui/motion';

/** One narrated step, as the caller's copy module defines it. */
export interface NarratedStep {
  /** The step's key — the engine's phase name. */
  phase: string;
  /** What we are doing. The status region announces this once, as the step starts. */
  label: string;
  /** The line under the label while the step is CURRENT (null: the label says it all). */
  now: string | null;
  /** The past-tense word that replaces the now-line once the step has ticked. */
  done: string;
  /** What it says instead, when its work did not happen (see "MISSED" above). */
  missed?: string;
}

type StepState = 'done' | 'missed' | 'current' | 'upcoming';

/**
 * How long the status region sits in the page EMPTY before its first words
 * (see ACCESSIBILITY above): long enough for it to be in the accessibility
 * tree as a live region before it changes, and well inside either checklist's
 * first step (0.9s+ on both production clocks).
 */
export const STATUS_ARM_MS = 150;

/**
 * The words a screen reader hears after each step. The state is carried by the
 * mark's SHAPE (tick / bar / turning ring / empty circle), the label's weight
 * and the line under it as well — never by colour alone (WCAG 1.4.1).
 */
const STEP_STATE_WORDS: Record<StepState, string> = {
  done: ', done',
  missed: ', not done',
  current: ', in progress',
  upcoming: ', not started yet',
};

/** 28px box so the marks line up whatever their shape. */
const MARK_BOX = 28;

function StepMark({ state, reduced }: { state: StepState; reduced: boolean }) {
  if (state === 'current') {
    return (
      <span aria-hidden="true" className="gps-jux-loader-mark" data-step-mark="current">
        <LoaderRing size={MARK_BOX} reduced={reduced} variant="thin" />
      </span>
    );
  }
  if (state === 'done' || state === 'missed') {
    const missed = state === 'missed';
    return (
      <span
        aria-hidden="true"
        className="gps-jux-loader-mark"
        data-step-mark={state}
        // Opacity only — and on a leaf that wraps nothing but its own mark.
        style={{ animation: reduced ? undefined : 'gps-jux-fade-in 280ms var(--gps-ease) both' }}
      >
        <svg width={MARK_BOX} height={MARK_BOX} viewBox="0 0 28 28" focusable="false" style={{ display: 'block' }}>
          {/* The light disc (the tick on it is 8.7:1; the missed bar on its
              warm disc 5.09:1 — tokens.css P8 / P11 contrast tables). */}
          <circle cx="14" cy="14" r="14" fill={missed ? 'var(--gps-jux-loader-missed-disc, #ece8e0)' : 'var(--gps-jux-loader-disc, #e6ede6)'} />
          {/* pathLength=1 makes the draw a unit dash: offset 1 → 0 draws the
              mark in. The reduced cohort gets no animation, so offset 0 — the
              finished mark — from the first paint. */}
          <path
            d={missed ? 'M9 14h10' : 'M8.5 14.5l3.6 3.6 7.4-7.6'}
            pathLength={1}
            strokeDasharray="1"
            fill="none"
            stroke={missed ? 'var(--gps-jux-moment-muted, #56645c)' : 'var(--gps-jux-loader-ink, #0a4a2e)'}
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={reduced ? undefined : { animation: 'gps-jux-tick-draw 420ms var(--gps-ease) 80ms both' }}
          />
        </svg>
      </span>
    );
  }
  return <span aria-hidden="true" className="gps-jux-loader-mark" data-step-mark="upcoming" />;
}

/** Where the white row sits: over the current step, or faded out once all are done. */
interface GlidePlacement {
  y: number;
  height: number;
  visible: boolean;
}

/**
 * Measure the current row so the white row can glide to it. Re-measured when the
 * step changes and whenever the list's box changes size (a web font landing, a
 * rotated phone, a line wrapping), so the row never sits between two steps.
 */
function useGlide(list: RefObject<HTMLOListElement | null>, current: number, done: boolean): GlidePlacement {
  const [placement, setPlacement] = useState<GlidePlacement>({ y: 0, height: 0, visible: false });
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const measure = () => {
      const row = el.children[current] as HTMLElement | undefined;
      if (!row) return;
      setPlacement({ y: row.offsetTop, height: row.offsetHeight, visible: !done });
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [list, current, done]);
  return placement;
}

export function NarratedChecklist({
  eyebrow,
  title,
  intro,
  allDoneLine,
  steps,
  phase,
  waitLine = null,
  missed = [],
  focusTitle = false,
}: {
  eyebrow: string;
  title: string;
  /** Under the heading while the steps run (replaced by `allDoneLine` at the end). */
  intro: string;
  /** The closing beat's line — the status region's last announcement. */
  allDoneLine: string;
  steps: ReadonlyArray<NarratedStep>;
  /** The current step's `phase`, or `'done'` for the closing beat. */
  phase: string;
  /** The visible + announced "late" line (still working / longer than usual), or null. */
  waitLine?: string | null;
  /** Phases whose work did not happen — drawn "missed", never ticked. */
  missed?: ReadonlyArray<string>;
  /** Move focus to the heading once, on mount (see the header). */
  focusTitle?: boolean;
}) {
  const reduced = usePrefersReducedMotion();
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const allDone = phase === 'done';
  const current = allDone
    ? steps.length - 1
    : Math.max(
        0,
        steps.findIndex((step) => step.phase === phase),
      );
  const glide = useGlide(listRef, current, allDone);
  const lateLine = allDone ? null : waitLine;
  // The step that has JUST finished — and, when its work did not happen, its
  // missed line, which leads the next announcement (see "MISSED" above).
  const justFinished = allDone ? steps[steps.length - 1] : current > 0 ? steps[current - 1] : undefined;
  const missedLine = justFinished && missed.includes(justFinished.phase) ? (justFinished.missed ?? null) : null;
  const stepLine = allDone ? allDoneLine : steps[current].label;
  const announcement = lateLine ?? (missedLine ? `${missedLine}. ${stepLine}` : stepLine);
  // Empty on mount, then its words (see ACCESSIBILITY above).
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setArmed(true), STATUS_ARM_MS);
    return () => clearTimeout(timer);
  }, []);

  // Once, on mount — never on a later step (that would pull focus back from
  // wherever the patient has since moved it); `focusTitle` never changes for a
  // mounted loader. `preventScroll`: the caller decides the scroll, and a focus
  // scroll would fight it.
  useEffect(() => {
    if (focusTitle) titleRef.current?.focus({ preventScroll: true });
  }, [focusTitle]);

  return (
    <>
      <div className="gps-jux-loader gps-jux-bloom">
        <p className="gps-jux-moment-eyebrow">{eyebrow}</p>
        <h1 id={titleId} ref={titleRef} className="gps-jux-moment-title" tabIndex={focusTitle ? -1 : undefined}>
          {title}
        </h1>
        <p className="gps-jux-loader-sub">{allDone ? allDoneLine : intro}</p>

        <div className="gps-jux-loader-steps" data-motion={reduced ? 'reduced' : 'full'}>
          <span
            aria-hidden="true"
            className="gps-jux-loader-glide"
            data-visible={glide.visible ? 'true' : 'false'}
            style={{ transform: `translateY(${glide.y}px)`, height: glide.height ? `${glide.height}px` : undefined }}
          />
          <ol ref={listRef} aria-labelledby={titleId} aria-busy={allDone ? 'false' : 'true'} className="gps-jux-loader-list">
            {steps.map((step, index) => {
              const finished = allDone || index < current;
              const state: StepState = finished
                ? missed.includes(step.phase)
                  ? 'missed'
                  : 'done'
                : index === current
                  ? 'current'
                  : 'upcoming';
              return (
                <li
                  key={step.phase}
                  className="gps-jux-loader-step"
                  // A missed step is FINISHED — the list has moved past it — so
                  // it lays out as done (its connector fills); the outcome is
                  // its own attribute, and its words and mark say which.
                  data-step-state={finished ? 'done' : state}
                  data-step-outcome={state === 'missed' ? 'missed' : undefined}
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  <StepMark state={state} reduced={reduced} />
                  <span className="gps-jux-loader-text">
                    <span className="gps-jux-loader-label">{step.label}</span>
                    {/* Both lines share ONE grid cell, so a row never changes
                        height when it ticks; CSS shows the one for the state.
                        A step with no now-line keeps its line box (nbsp). */}
                    <span className="gps-jux-loader-line">
                      <span data-line="now">{step.now ?? '\u00a0'}</span>
                      <span data-line="done">{state === 'missed' ? (step.missed ?? step.done) : step.done}</span>
                    </span>
                  </span>
                  <span className="gps-sr-only">{STEP_STATE_WORDS[state]}</span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* The visible late line. The status region below is what announces
            it, so this copy is hidden from assistive tech; its box is reserved
            (tokens.css) so nothing moves when it arrives. Keyed on the words,
            so a second, calmer line fades in rather than snapping. */}
        <p className="gps-jux-loader-still" aria-hidden="true">
          {lateLine ? <span key={lateLine}>{lateLine}</span> : null}
        </p>
      </div>
      <p role="status" className="gps-sr-only">
        {armed ? announcement : null}
      </p>
    </>
  );
}
